import React, { useState, useEffect, useMemo } from 'react';

import { Box, Grid, CircularProgress, Alert, Typography, Chip } from '@mui/material';

import { useHistory, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import PageHeader from '../../components/PageHeader';

import SectionCard from '../../components/SectionCard';

import StatusChip from '../../components/StatusChip';

import InvButton from '../../components/InvButton';

import ConfirmDialog from '../../components/ConfirmDialog';

import VendorWorkflowBar from '../../components/material/VendorWorkflowBar';

import { DetailRow } from '../../components/InvField';

import { getVendor, fromApiVendor } from '../../api/vendor';

import { getPurchaseOrders } from '../../api/procurement';

import useVendorWorkflow from '../../hooks/useVendorWorkflow';

import InvDataGrid from '../../components/InvDataGrid';

import { formatDate } from '../../utils';

import { documentTypeLabel, vendorSubmitBlockReason } from '../../utils/vendorEntity';

import { canEditVendor, isPurchasableVendor } from '../../utils/vendorWorkflow';

import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';



function DocumentsSummary({ documents }) {

  const docs = documents && typeof documents === 'object' ? documents : {};

  const entries = Object.entries(docs).filter(([, val]) => val && typeof val === 'object');



  if (entries.length === 0) {

    return (

      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>

        No supporting documents attached.

      </Typography>

    );

  }



  return (

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {entries.map(([key, doc]) => (

        <Box key={key} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>

          <Typography sx={{ fontWeight: 600, fontSize: 13, minWidth: 160 }}>

            {documentTypeLabel(key)}

          </Typography>

          {doc.document_number && (

            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>

              {doc.document_number}

            </Typography>

          )}

          {doc.file_name && (

            <Chip label={doc.file_name} size="small"

              sx={{

                backgroundColor: IPD_COLORS.selectedBg,

                color: IPD_COLORS.primary,

                fontWeight: 600,

                fontSize: 11,

              }} />

          )}

        </Box>

      ))}

    </Box>

  );

}



export default function VendorDetail() {

  const { can } = useInvRole();

  const { id } = useParams();

  const { url } = useRouteMatch();

  const history = useHistory();

  const location = useLocation();

  const [vendor, setVendor] = useState(null);

  const [pos, setPos] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [dialog, setDialog] = useState(null);

  const [reason, setReason] = useState('');



  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const justSaved = query.get('saved') === '1';



  async function load() {

    setLoading(true);

    setError(null);

    try {

      const [v, poData] = await Promise.all([

        getVendor(id),

        getPurchaseOrders({ vendor_id: id }).catch(() => []),

      ]);

      setVendor({ ...v, ...fromApiVendor(v), documents: v.documents || {} });

      setPos(Array.isArray(poData) ? poData : (poData?.items || []));

    } catch (e) {

      setError(e.message);

    } finally {

      setLoading(false);

    }

  }



  useEffect(() => { load(); }, [id]);



  const { executeAction, loading: actionLoading, activeAction } = useVendorWorkflow(id, {

    vendor,

    onSuccess: () => load(),

  });



  function handleActionClick(action) {

    if (action.key === 'submit') {
      const msg = vendorSubmitBlockReason(vendor);
      if (msg) {
        toast.error(msg);
        history.push(`${url}/edit`);
        return;
      }
    }

    setDialog({

      action: action.key,

      title: action.title,

      desc: action.description,

      destructive: action.destructive,

      requireReason: action.requireReason,

    });

  }



  async function confirmAction() {

    if (!dialog) return;

    try {

      await executeAction(dialog.action, { reason: reason.trim() });

      setDialog(null);

      setReason('');

      if (justSaved) {
        history.replace(`${url}`);
      }

    } catch {

      setDialog(null);

    }

  }



  if (loading) {

    return (

      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>

        <CircularProgress sx={{ color: IPD_COLORS.primary }} />

      </Box>

    );

  }

  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;

  if (!vendor) return null;



  const editable = canEditVendor(vendor) && can(INV_ACTIONS.VENDOR_EDIT);

  const purchasable = isPurchasableVendor(vendor);
  const materialBase = url.replace(/\/vendors\/[^/]+$/, '');



  const poColumns = [

    { field: 'po_number', headerName: 'PO #', width: 140 },

    { field: 'status', headerName: 'Status', width: 130, renderCell: p => <StatusChip status={p.value} /> },

    { field: 'expected_delivery_date', headerName: 'Expected', width: 120, valueFormatter: p => formatDate(p.value) },

    { field: 'created_at', headerName: 'Created', width: 120, valueFormatter: p => formatDate(p.value) },

  ];



  const fullAddress = [vendor.address, vendor.address_line2, vendor.city, vendor.state, vendor.country, vendor.pincode]

    .filter(Boolean)

    .join(', ');



  return (

    <Box>

      <PageHeader

        eyebrow="Material Management › Vendors"

        title={vendor.vendor_name}

        description={[vendor.vendor_code, vendor.legal_name].filter(Boolean).join(' · ')}

        actions={

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>

            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>

              Back

            </InvButton>

            {editable && (
              <InvButton variant="outlined" tone="teal" size="small"
                onClick={() => history.push(`${url}/edit`)}>
                Edit
              </InvButton>
            )}
            {purchasable && (
              <>
                {can(INV_ACTIONS.RFQ_CREATE) && (
                  <InvButton
                    size="small"
                    tone="mint"
                    onClick={() => history.push(`${materialBase}/rfq/new?vendor_id=${id}`)}
                  >
                    Create RFQ
                  </InvButton>
                )}
                {can(INV_ACTIONS.PO_CREATE) && (
                  <InvButton
                    size="small"
                    onClick={() => history.push(`${materialBase}/po/new?vendor_id=${id}`)}
                  >
                    Create PO
                  </InvButton>
                )}
              </>
            )}

          </Box>

        }

      />



      {justSaved && (
        <Alert severity="success" sx={{ mb: 2.5 }}>
          Vendor saved as <strong>Draft</strong>. Review details and documents below, then use
          {' '}<strong>Submit for Approval</strong> when ready.
        </Alert>
      )}



      <VendorWorkflowBar

        vendor={vendor}

        onActionClick={handleActionClick}

        actionLoading={actionLoading}

        activeAction={activeAction}

      />



      <Grid container spacing={2.5}>

        <Grid item xs={12} md={6}>

          <SectionCard

            title="Vendor Details"

            actions={(

              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>

                <StatusChip status={vendor.approval_status} />

              </Box>

            )}

          >

            <DetailRow label="Code">{vendor.vendor_code}</DetailRow>

            <DetailRow label="Legal Name">{vendor.legal_name}</DetailRow>

            <DetailRow label="Type">{vendor.vendor_type}</DetailRow>

            <DetailRow label="Category">{vendor.vendor_category}</DetailRow>

            <DetailRow label="Rating">{vendor.rating || '—'}</DetailRow>

            <DetailRow label="Contact">{vendor.contact_person}</DetailRow>

            <DetailRow label="Email">{vendor.email}</DetailRow>

            <DetailRow label="Phone">{vendor.phone}</DetailRow>

            <DetailRow label="Alt. Phone">{vendor.alternate_phone}</DetailRow>

            <DetailRow label="Address">{fullAddress || '—'}</DetailRow>

            <DetailRow label="GSTIN">{vendor.gstin}</DetailRow>

            <DetailRow label="PAN">{vendor.pan}</DetailRow>

            <DetailRow label="Drug License">{vendor.drug_license_no || '—'}</DetailRow>

            <DetailRow label="License Valid">{vendor.license_valid_till ? formatDate(vendor.license_valid_till) : '—'}</DetailRow>

          </SectionCard>

        </Grid>

        <Grid item xs={12} md={6}>

          <SectionCard title="Commercial">

            <DetailRow label="Payment Terms">{vendor.payment_terms}</DetailRow>

            <DetailRow label="Credit Period (Days)">

              {vendor.credit_period !== '' && vendor.credit_period != null ? vendor.credit_period : '—'}

            </DetailRow>

            <DetailRow label="Currency">{vendor.currency}</DetailRow>

            <DetailRow label="Can Purchase">

              <Typography component="span" sx={{

                fontSize: 13,

                fontWeight: 600,

                color: purchasable ? IPD_COLORS.success.text : IPD_COLORS.error.text,

              }}>

                {purchasable ? 'Yes — eligible for PO' : 'No'}

              </Typography>

            </DetailRow>

            <DetailRow label="Bank Name">{vendor.bank_name}</DetailRow>

            <DetailRow label="Account">{vendor.bank_account}</DetailRow>

            <DetailRow label="IFSC">{vendor.bank_ifsc}</DetailRow>

          </SectionCard>

          <SectionCard title="Remarks" sx={{ mt: 2.5 }}>

            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>

              {vendor.remarks || '—'}

            </Typography>

          </SectionCard>

        </Grid>

        <Grid item xs={12}>

          <SectionCard title="Supporting Documents">

            <DocumentsSummary documents={vendor.documents} />

          </SectionCard>

        </Grid>

        <Grid item xs={12}>

          <SectionCard title="Purchase Orders">

            <InvDataGrid rows={pos} columns={poColumns} loading={false}

              getRowId={r => r.purchase_order_id || r.id} emptyMessage="No purchase orders for this vendor." />

          </SectionCard>

        </Grid>

      </Grid>



      <ConfirmDialog

        open={Boolean(dialog)}

        title={dialog?.title}

        description={dialog?.desc}

        destructive={dialog?.destructive}

        requireReason={dialog?.requireReason}

        reasonValue={reason}

        onReasonChange={setReason}

        loading={actionLoading}

        onConfirm={confirmAction}

        onCancel={() => { setDialog(null); setReason(''); }}

      />

    </Box>

  );

}


